import { getRequest, postRequest } from "../RestServices";
import {
  ReportsCameraData,
  ReportsDeviceData,
  ReportsOpportunityHistory,
  ReportsVTUData,
  Reports360JobsData,
  Reports360WorkticketsData,
  Reports360JobsCsvData,
  Reports360WorkticketsCsvData,
  ReportsDisplaySwapReportCsvData,
  ReportsDisplaySwapReportData,
} from "../../api/reports/url";
import { CFWARRANTYSTATUS } from "../../api/VTU/url";
import { getToken } from "../../utility/tokenOperations";

const buildSwapRequestOptions = (acceptHeader) => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: acceptHeader,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return {
    headers,
  };
};

const executeWithFallback = async ({ primaryUrl, fallbackUrl, payload, requestOptions, validateFn }) => {
  try {
    const response = await postRequest(primaryUrl, payload, requestOptions);
    if (validateFn(response)) {
      return response;
    }
  } catch (error) {
    const status = error?.response?.status;
    if (fallbackUrl === primaryUrl || status === 401 || status === 403 || (status && status >= 500)) {
      throw error?.response?.data ?? error;
    }
  }

  if (fallbackUrl !== primaryUrl) {
    try {
      const fallbackResponse = await postRequest(fallbackUrl, payload, requestOptions);
      if (validateFn(fallbackResponse)) {
        return fallbackResponse;
      }
    } catch (fallbackError) {
      throw fallbackError?.response?.data ?? fallbackError;
    }
  }

  throw new Error("Invalid response structure received from API endpoints");
};

export const opportunityHistory = async (payload) => {
  try {
    const _response = await postRequest(ReportsOpportunityHistory, payload);
    const _OpportunityDetails = _response?.data?.data ?? [];
    return { rows: _OpportunityDetails };
  } catch (err) {
    return err;
  }
};

export const fetchProductInfo = async (payload) => {
  try {
    return await getRequest(`${ReportsOpportunityHistory}/${payload}/productdetails`);
  } catch (e) {
    return e;
  }
};

export const retrieveDeviceData = async (payload) => {
  try {
    return await postRequest(ReportsDeviceData, payload);
  } catch (err) {
    return err;
  }
};

export const retrieveVtuPageData = (payload, pageNo = 0, pageSize = 10) =>
  new Promise((res, rej) => {
    postRequest(`${ReportsVTUData}?pageNo=${pageNo}&pageSize=${pageSize}`, payload)
      .then((data) => res(data?.data?.data))
      .catch(({ response }) => rej(response.data));
  });

export const retrieveCameraPageData = (payload, pageNo = 0, pageSize = 10) =>
  new Promise((res, rej) => {
    postRequest(`${ReportsCameraData}?pageNo=${pageNo}&pageSize=${pageSize}`, payload)
      .then((data) => res(data?.data?.data))
      .catch(({ response }) => rej(response?.data));
  });

export const checkCFStatus = (payload) =>
  new Promise((res, rej) => {
    postRequest(CFWARRANTYSTATUS, payload)
      .then((data) => res(data?.data?.data))
      .catch(({ response }) => rej(response?.data));
  });

export const retrieveJobsPageData = (payload, pageNo = 0, pageSize = 10) =>
  new Promise((res, rej) => {
    postRequest(`${Reports360JobsData}?pageNo=${pageNo}&pageSize=${pageSize}`, payload)
      .then((data) => res(data?.data?.data))
      .catch(({ response }) => rej(response?.data));
  });

export const retrieveWorkticketsPageData = (payload, pageNo = 0, pageSize = 10) =>
  new Promise((res, rej) => {
    postRequest(`${Reports360WorkticketsData}?pageNo=${pageNo}&pageSize=${pageSize}`, payload)
      .then((data) => res(data?.data?.data))
      .catch(({ response }) => rej(response?.data));
  });

export const retrieveJobsCsvPageData = async (payload) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/zip",
  };
  // Ensure responseType: 'blob' is set for Axios
  const response = await postRequest(Reports360JobsCsvData, payload, { headers, responseType: "blob" });

  // Axios puts the blob in response.data if responseType is 'blob'
  if (response && response.data instanceof Blob) {
    return response;
  }
  throw new Error("No valid ZIP or Blob data returned");
};

export const retrieveWorkticketsCsvPageData = async (payload) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/zip",
  };
  // Ensure responseType: 'blob' is set for Axios
  const response = await postRequest(Reports360WorkticketsCsvData, payload, { headers, responseType: "blob" });

  // Axios puts the blob in response.data if responseType is 'blob'
  if (response && response.data instanceof Blob) {
    return response;
  }
  throw new Error("No valid ZIP or Blob data returned");
};

export const retrieveSwapReportData = async (payload, pageNo = 0, pageSize = 10, location = "INTL") => {
  const primaryUrl = `${ReportsDisplaySwapReportData}?pageNo=${pageNo}&pageSize=${pageSize}&location=${location}`;
  const configuredFallback = process.env.REACT_APP_SWAP_REPORT_FALLBACK_URL;
  const fallbackUrl = configuredFallback ? `${configuredFallback}?pageNo=${pageNo}&pageSize=${pageSize}&location=${location}` : primaryUrl;

  const requestOptions = buildSwapRequestOptions("application/json");

  const response = await executeWithFallback({
    primaryUrl,
    fallbackUrl,
    payload,
    requestOptions,
    validateFn: (res) => res && Array.isArray(res?.data?.data?.swaps),
  });

  return response.data.data;
};

export const retrieveSwapReportCsvPageData = async (payload, location = "INTL") => {
  const primaryUrl = `${ReportsDisplaySwapReportCsvData}?location=${location}`;
  const configuredFallback = process.env.REACT_APP_SWAP_REPORT_CSV_FALLBACK_URL;
  const fallbackUrl = configuredFallback ? `${configuredFallback}?location=${location}` : primaryUrl;

  const requestOptions = {
    ...buildSwapRequestOptions("text/csv"),
    responseType: "blob",
  };

  const isCsvBlob = (response) => {
    if (!(response && response.data instanceof Blob)) return false;
    const contentTypeHeader = String(response?.headers?.["content-type"] || "").toLowerCase();
    const blobType = String(response?.data?.type || "").toLowerCase();
    return !contentTypeHeader.includes("text/html") && !blobType.includes("text/html");
  };

  return await executeWithFallback({
    primaryUrl,
    fallbackUrl,
    payload,
    requestOptions,
    validateFn: isCsvBlob,
  });
};
